/**
 * Admin Prospects API client.
 * Thin wrappers around the worker endpoints in src/api/admin-prospects.ts
 * and src/api/admin-prospect-settings.ts.
 */

import type {
  ProspectListRow,
  KanbanColumn,
  ProspectDetailResponse,
  ConvertResult,
  Stage,
  Task,
  ConsentDisclosure,
  StageStatus,
  TaskStatus,
} from './types';

const API_BASE = import.meta.env.VITE_API_URL;

async function authedFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string; [k: string]: any }> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const text = await resp.text();
  let body: any;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { success: false, error: `Non-JSON response (${resp.status})` };
  }
  if (!resp.ok && body.success !== false) {
    body.success = false;
    body.error = body.error || `Request failed (${resp.status})`;
  }
  return body;
}

// =============================================================================
// Prospects — list / kanban / detail / actions
// =============================================================================

export interface ProspectsListParams {
  limit?: number;
  offset?: number;
  stage_id?: string;
  status?: string;
  affiliate_id?: string;
  archived?: boolean;
  converted?: boolean;
  search?: string;
}

export async function getProspects(params: ProspectsListParams = {}) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      search.set(k, String(v));
    }
  }
  return authedFetch<ProspectListRow[]>(`/admin/prospects?${search.toString()}`);
}

export async function getKanban() {
  return authedFetch<{ stages: KanbanColumn[] }>(`/admin/prospects/kanban`);
}

export async function getProspectDetail(id: string) {
  return authedFetch<ProspectDetailResponse>(`/admin/prospects/${id}`);
}

export async function updateProspect(id: string, updates: Record<string, any>) {
  return authedFetch(`/admin/prospects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function transitionProspectStage(
  id: string,
  to_stage_id: string,
  to_stage_status: StageStatus,
  note?: string
) {
  return authedFetch<{ event_id: string }>(`/admin/prospects/${id}/stage`, {
    method: 'POST',
    body: JSON.stringify({ to_stage_id, to_stage_status, note: note || null }),
  });
}

export async function markProspectTask(
  prospectId: string,
  taskId: string,
  to_status: TaskStatus,
  note?: string
) {
  return authedFetch<{ task_status_id: string }>(
    `/admin/prospects/${prospectId}/tasks/${taskId}`,
    {
      method: 'POST',
      body: JSON.stringify({ to_status, note: note || null }),
    }
  );
}

export async function convertProspect(id: string, note?: string) {
  return authedFetch<ConvertResult>(`/admin/prospects/${id}/convert`, {
    method: 'POST',
    body: JSON.stringify({ note: note || null }),
  });
}

export async function archiveProspect(id: string, reason: string) {
  return authedFetch(`/admin/prospects/${id}/archive`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function resendMagicLink(id: string) {
  return authedFetch<{
    magic_link_id: string;
    email_sent: boolean;
    email_warning: string | null;
    magic_link_url?: string;
  }>(`/admin/prospects/${id}/resend-magic-link`, {
    method: 'POST',
  });
}

export async function uploadProspectDocument(
  id: string,
  file: File,
  documentType: 'ss4' | 'other' = 'ss4'
) {
  const form = new FormData();
  form.append('file', file);
  form.append('document_type', documentType);
  const resp = await fetch(`${API_BASE}/admin/prospects/${id}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await resp.text();
  let body: any;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { success: false, error: `Non-JSON response (${resp.status})` };
  }
  if (!resp.ok && body.success !== false) {
    body.success = false;
    body.error = body.error || `Upload failed (${resp.status})`;
  }
  return body;
}

export function getProspectDocumentDownloadUrl(prospectId: string, docId: string): string {
  return `${API_BASE}/admin/prospects/${prospectId}/documents/${docId}/download`;
}

export function getProspectDocumentViewUrl(prospectId: string, docId: string): string {
  return `${API_BASE}/admin/prospects/${prospectId}/documents/${docId}/download?disposition=inline`;
}

// =============================================================================
// Settings — stages / tasks / disclosures
// =============================================================================

export async function getStages(includeArchived = false) {
  const q = includeArchived ? '?archived=true' : '';
  return authedFetch<Stage[]>(`/admin/prospects/config/stages${q}`);
}

export async function createStage(payload: Partial<Stage>) {
  return authedFetch<Stage>(`/admin/prospects/config/stages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateStage(id: string, updates: Partial<Stage> & { archived?: boolean }) {
  return authedFetch<Stage>(`/admin/prospects/config/stages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function getTasks(includeArchived = false) {
  const q = includeArchived ? '?archived=true' : '';
  return authedFetch<Task[]>(`/admin/prospects/config/tasks${q}`);
}

export async function createTask(payload: Partial<Task>) {
  return authedFetch<Task>(`/admin/prospects/config/tasks`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateTask(id: string, updates: Partial<Task> & { archived?: boolean }) {
  return authedFetch<Task>(`/admin/prospects/config/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function getDisclosures(includeRetired = false) {
  const q = includeRetired ? '?include_retired=true' : '';
  return authedFetch<ConsentDisclosure[]>(`/admin/prospects/config/disclosures${q}`);
}

export async function createDisclosure(payload: {
  policy_key: string;
  text_content: string;
  url?: string | null;
}) {
  return authedFetch<ConsentDisclosure>(`/admin/prospects/config/disclosures`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
